import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { setActiveProjectId } from '@/shared/api/active-project';

const SELECTED_PROJECT_STORAGE_KEY = 'iv-selected-project-id';
const SELECTED_PROJECT_EVENT = 'iv-selected-project-change';

export interface CurrentProject {
  /** Project id from the current route, or the topbar-selected project when not on a project route. */
  projectId: string | undefined;
  /** Same value coerced to a number, or `undefined` if missing/non-numeric. */
  projectIdNumber: number | undefined;
  /** Raw project id parsed from the route only. */
  routeProjectId: string | undefined;
  routeProjectIdNumber: number | undefined;
  /** Project name parsed from project-name routes such as team/surveillance. */
  routeProjectName: string | undefined;
  selectedProjectId: string | undefined;
  selectedProjectIdNumber: number | undefined;
  setSelectedProjectId: (projectId: string | number | null | undefined) => void;
  clearSelectedProject: () => void;
  isProjectRoute: boolean;
}

const parseProjectRoute = (pathname: string) => {
  const propertyWithClientMatch = pathname.match(
    /\/app\/property\/[^/]+\/(\d+)\/(dashboard|progress|asbuilds|drones|drawings|punch-lists|observations|safety|rfis|submittals|photos|files|daily-log|meeting-minutes)(?:\/|$)/,
  );
  const propertyLegacyMatch = pathname.match(
    /\/app\/property\/(\d+)\/(dashboard|progress|asbuilds|drones|drawings|punch-lists|observations|safety|rfis|submittals|photos|files|daily-log|meeting-minutes)(?:\/|$)/,
  );
  const namedProjectMatch = pathname.match(/\/app\/(?:surveillance|team)\/[^/]+\/([^/]+)/);

  return {
    projectId: propertyWithClientMatch?.[1] ?? propertyLegacyMatch?.[1],
    projectName: namedProjectMatch?.[1] ? decodeURIComponent(namedProjectMatch[1]) : undefined,
  };
};

const coerceProjectId = (projectId: string | undefined) => {
  if (!projectId) return undefined;
  const parsed = Number(projectId);
  return Number.isFinite(parsed) ? parsed : undefined;
};

/**
 * Read the current project from the route and/or topbar selection. Route
 * project ids are registered with the API request wrapper; manually selected
 * projects are UI context only until the user navigates into a project route.
 *
 * Drop-in replacement for `useParams<{ projectId: string }>()` on
 * project-scoped pages — the return type matches `useParams` semantics
 * (`undefined` when missing) so existing call sites keep compiling.
 */
export const useCurrentProject = (): CurrentProject => {
  const { projectId: routeParamProjectId } = useParams<{ projectId: string }>();
  const location = useLocation();
  const routeProject = useMemo(() => parseProjectRoute(location.pathname), [location.pathname]);
  const routeProjectId = routeParamProjectId ?? routeProject.projectId;
  const routeProjectName = routeProject.projectName;
  const [selectedProjectId, setSelectedProjectIdState] = useState<string | undefined>(() => {
    const stored = localStorage.getItem(SELECTED_PROJECT_STORAGE_KEY);
    return stored || undefined;
  });

  useEffect(() => {
    const handleSelectionChange = () => {
      const stored = localStorage.getItem(SELECTED_PROJECT_STORAGE_KEY);
      setSelectedProjectIdState(stored || undefined);
    };

    window.addEventListener(SELECTED_PROJECT_EVENT, handleSelectionChange);
    window.addEventListener('storage', handleSelectionChange);

    return () => {
      window.removeEventListener(SELECTED_PROJECT_EVENT, handleSelectionChange);
      window.removeEventListener('storage', handleSelectionChange);
    };
  }, []);

  const setSelectedProjectId = useCallback((projectId: string | number | null | undefined) => {
    const nextValue = projectId == null || projectId === '' ? undefined : String(projectId);
    if (nextValue) {
      localStorage.setItem(SELECTED_PROJECT_STORAGE_KEY, nextValue);
    } else {
      localStorage.removeItem(SELECTED_PROJECT_STORAGE_KEY);
    }
    setSelectedProjectIdState(nextValue);
    window.dispatchEvent(new Event(SELECTED_PROJECT_EVENT));
  }, []);

  const clearSelectedProject = useCallback(() => {
    setSelectedProjectId(null);
  }, [setSelectedProjectId]);

  useEffect(() => {
    if (routeProjectId) {
      setSelectedProjectId(routeProjectId);
    }
  }, [routeProjectId, setSelectedProjectId]);

  const effectiveProjectId = routeProjectId ?? selectedProjectId;
  const projectIdNumber = useMemo(() => coerceProjectId(effectiveProjectId), [effectiveProjectId]);
  const routeProjectIdNumber = useMemo(() => coerceProjectId(routeProjectId), [routeProjectId]);
  const selectedProjectIdNumber = useMemo(() => coerceProjectId(selectedProjectId), [selectedProjectId]);

  useEffect(() => {
    setActiveProjectId(routeProjectId ?? null);
  }, [routeProjectId]);

  return {
    projectId: effectiveProjectId,
    projectIdNumber,
    routeProjectId,
    routeProjectIdNumber,
    routeProjectName,
    selectedProjectId,
    selectedProjectIdNumber,
    setSelectedProjectId,
    clearSelectedProject,
    isProjectRoute: Boolean(routeProjectId || routeProjectName),
  };
};
